(() => {
  'use strict';

  /*
   * AONSK checkout intent and recovery layer, V10.38.1.
   *
   * The normal path is Shopify's native POST cart form. This script never waits
   * for a cart refresh before checkout. Its first job is to tell the cart drawer
   * that a shopper has started checkout so no asynchronous cart render can
   * remove the exact form or button underneath the shopper's finger.
   */

  const cartUrl = window.theme?.routes?.cart || '/cart';
  const checkoutSelector = 'button[name="checkout"], input[name="checkout"]';
  const fallbackDelay = 3500;
  let navigationStarted = false;
  let fallbackTimer = null;
  let intentClearTimer = null;

  window.__AONSK_CHECKOUT_DIAGNOSTICS__ = window.__AONSK_CHECKOUT_DIAGNOSTICS__ || {};

  const emitIntent = (active, control = null, reason = '') => {
    window.__aonskCheckoutIntentActive = active;
    window.__AONSK_CHECKOUT_DIAGNOSTICS__.checkoutIntent = active;
    window.__AONSK_CHECKOUT_DIAGNOSTICS__.checkoutIntentAt = active ? Date.now() : null;
    window.__AONSK_CHECKOUT_DIAGNOSTICS__.checkoutIntentReason = reason;

    document.dispatchEvent(new CustomEvent(active ? 'aonsk:checkout-intent' : 'aonsk:checkout-intent-clear', {
      detail: {
        control,
        reason,
        at: Date.now()
      }
    }));

    if (intentClearTimer) window.clearTimeout(intentClearTimer);
    if (active) {
      // If navigation is cancelled by a browser, app, or validation rule, allow
      // cart interactions to recover rather than leaving the drawer frozen.
      intentClearTimer = window.setTimeout(() => emitIntent(false, null, 'intent-expired'), 12000);
    }
  };

  const markNavigation = () => {
    navigationStarted = true;
    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  window.addEventListener('pagehide', markNavigation, { capture: true });
  window.addEventListener('beforeunload', markNavigation, { capture: true });
  window.addEventListener('pageshow', () => emitIntent(false, null, 'page-show'), { capture: true });

  const associatedForm = (control) => {
    if (!control) return null;
    if (control.form instanceof HTMLFormElement) return control.form;
    const formId = control.getAttribute?.('form');
    if (formId) {
      const referenced = document.getElementById(formId);
      if (referenced instanceof HTMLFormElement) return referenced;
    }
    const closest = control.closest?.('form');
    return closest instanceof HTMLFormElement ? closest : null;
  };

  const isNativeCartForm = (form) => {
    if (!(form instanceof HTMLFormElement) || !form.isConnected) return false;
    if ((form.method || 'get').toLowerCase() !== 'post') return false;
    try {
      const action = new URL(form.action, window.location.origin);
      const expected = new URL(cartUrl, window.location.origin);
      return action.origin === window.location.origin && action.pathname === expected.pathname;
    } catch (error) {
      return false;
    }
  };

  const termsAreSatisfied = (form) => {
    const selector = 'input[name="checkout[terms]"]';
    const inForm = form.querySelector(selector);
    const associated = form.id
      ? Array.from(document.querySelectorAll(selector)).find((node) => node.getAttribute('form') === form.id)
      : null;
    const checkbox = inForm || associated;
    if (!checkbox || checkbox.checked) return true;
    checkbox.focus({ preventScroll: false });
    checkbox.reportValidity?.();
    return false;
  };

  const removeExistingFallback = () => {
    document.querySelectorAll('[data-aonsk-checkout-fallback]').forEach((node) => node.remove());
  };

  const directNativeSubmit = (form) => {
    if (!isNativeCartForm(form)) {
      emitIntent(false, null, 'invalid-direct-form');
      window.location.assign(cartUrl);
      return;
    }
    if (!form.checkValidity()) {
      emitIntent(false, null, 'invalid-form-fields');
      form.reportValidity();
      return;
    }
    if (!termsAreSatisfied(form)) {
      emitIntent(false, null, 'terms-not-satisfied');
      return;
    }

    form.querySelectorAll('input[data-aonsk-direct-checkout]').forEach((node) => node.remove());
    const checkoutMarker = document.createElement('input');
    checkoutMarker.type = 'hidden';
    checkoutMarker.name = 'checkout';
    checkoutMarker.value = 'Checkout';
    checkoutMarker.dataset.aonskDirectCheckout = 'true';
    form.appendChild(checkoutMarker);

    emitIntent(true, null, 'direct-native-submit');
    navigationStarted = true;
    HTMLFormElement.prototype.submit.call(form);
  };

  const showFallback = (control, form, reason) => {
    emitIntent(false, null, reason);
    if (!control?.isConnected) return;
    const existing = control.parentElement?.querySelector('[data-aonsk-checkout-fallback]');
    if (existing) return;

    const fallback = document.createElement('div');
    fallback.className = 'aonsk-checkout-fallback';
    fallback.dataset.aonskCheckoutFallback = 'true';
    fallback.setAttribute('role', 'alert');
    fallback.innerHTML = `
      <strong>Checkout did not open.</strong>
      <span>Your cart is still saved in this browser.</span>
      <div class="aonsk-checkout-fallback__actions">
        <button type="button" class="aonsk-checkout-fallback__direct">Try checkout directly</button>
        <a href="${cartUrl}" data-no-instant>View full cart</a>
      </div>`;

    fallback.querySelector('.aonsk-checkout-fallback__direct')?.addEventListener('click', () => {
      directNativeSubmit(form);
    });

    control.insertAdjacentElement('afterend', fallback);

    try {
      window.dataLayer?.push({
        event: 'aonsk_checkout_fallback_shown',
        reason,
        path: window.location.pathname
      });
    } catch (error) {
      // Analytics must never affect checkout recovery.
    }
  };

  const markCheckoutIntentFromEvent = (event, reason) => {
    const control = event.target.closest?.(checkoutSelector);
    if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true') return;
    const form = associatedForm(control);
    if (isNativeCartForm(form)) emitIntent(true, control, reason);
  };

  // Pointer intent happens before click/default form submission. Marking it in
  // capture phase prevents a late cart-section response from replacing the form
  // between the shopper's press and release.
  document.addEventListener('pointerdown', (event) => {
    markCheckoutIntentFromEvent(event, 'pointerdown');
  }, true);
  document.addEventListener('touchstart', (event) => {
    markCheckoutIntentFromEvent(event, 'touchstart');
  }, { capture: true, passive: true });
  document.addEventListener('mousedown', (event) => {
    markCheckoutIntentFromEvent(event, 'mousedown');
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      markCheckoutIntentFromEvent(event, 'keyboard');
    }
  }, true);

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.(checkoutSelector);
    if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true') return;

    removeExistingFallback();
    navigationStarted = false;
    const form = associatedForm(control);

    if (!isNativeCartForm(form)) {
      event.preventDefault();
      emitIntent(false, null, 'invalid-associated-form');
      window.location.assign(cartUrl);
      return;
    }

    emitIntent(true, control, 'click');

    // Do not prevent, stop, delay, refresh, or re-submit. The browser performs
    // Shopify's native form submission. This timer only reveals recovery if the
    // page stays visible and unchanged afterward.
    const startUrl = window.location.href;
    fallbackTimer = window.setTimeout(() => {
      fallbackTimer = null;
      if (!navigationStarted && document.visibilityState === 'visible' && window.location.href === startUrl) {
        showFallback(control, form, 'checkout-stalled');
      }
    }, fallbackDelay);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!isNativeCartForm(form)) return;
    const submitter = event.submitter || document.activeElement;
    if (submitter?.getAttribute?.('name') !== 'checkout') return;

    emitIntent(true, submitter, 'submit');

    window.setTimeout(() => {
      if (event.defaultPrevented && submitter?.isConnected) {
        showFallback(submitter, form, 'checkout-cancelled');
      }
    }, 0);
  }, true);
})();
